import { BCMSRecord, ModalOpen, useBCMSContext, useBCMSModal, useParams, useRoute } from "@bcm-dev/bcms-vite";
import MainContentContainer, { MainContentHeader, MainContentSection } from "components/shared/containers/MainContentContainer";
import ControlView from "components/shared/views/BaseRowView/components/ControlView";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/ui/CustomAccordion";
import { UICancelButton } from "components/ui/UIButton";
import Classes, { Card, Transaction } from "model";

import React, { useState } from "react";
import { SVGAsset } from "res";
import "./styles.css";

/**
 * @function PaymentsPage
 * @param {String} currentBalance - Sets the current balance for the payment 
 * @param {String} statementBalance - Sets the statement balance for the payment 
 * @param {String} minimumDue - Sets the minimum balance due for the payment 
 * @param {String} dueDate - Sets the due date for the payment 
 * @return {React.Component}
 * @exported true
 * @description The main component for PaymentsPage
 */

export default function PaymentsPage(props) {

  const cardID = useParams().cardID ?? "1";
  const globalController = useBCMSContext();
  const navigate = useRoute();
  BCMSRecord.globalControllerFor([ Card,Transaction ], globalController);
  const selectedCard = Card.initByID(cardID);
  const payments = Transaction.getPaymentsByCard(cardID);

  const paymentRows =    payments.map(payment => <PaymentActivityRow payment={payment}/>)
  const currentBalance = selectedCard.balance.format() ?? "[$X.XX]";
  const statementBalance = selectedCard.balance.format()  ?? "[$X.XX]";
  const minimumDue = selectedCard.minPaymentAmount.format()  ?? "[$X.XX]";
  const dueDate = selectedCard.dateDue.format("M d, Y") ?? "[MMM DD, YYYY]";

  return(
    <MainContentContainer className="payments-page" header={<MainContentHeader text="Payments" />}>
      <div className="upper-content-container">
        <p>Select the reason you are disputing this transaction. Feel free to include additional comments to give us more content.</p>
        <div className="payment-detail-row flex-container flex-horizontal align-center-container column-space-between-container">
          <p className="grey-label">Current Balance</p>
          <p>{currentBalance}</p>
        </div>
        <div className="payment-detail-row flex-container flex-horizontal align-center-container column-space-between-container">
          <p className="grey-label">Statement Balance</p>
          <p>{statementBalance}</p>
        </div>
        <div className="payment-detail-row flex-container flex-horizontal align-center-container column-space-between-container">
          <p className="grey-label"  >Minimum Due</p>
          <p>{minimumDue}</p>
        </div>
        <div className="payment-detail-row flex-container flex-horizontal align-center-container column-space-between-container">
          <p className="grey-label">Due Date</p>
          <p>{dueDate}</p>
        </div>

        <div className="flex-container flex-horizontal align-center-container">
          <SVGAsset id="InformationIcon" className="set-width left-icon-container info-icon" />
          <p className="sub-text">Payment of {minimumDue} due on {dueDate}.</p>
        </div>
      </div>

      <ControlView controlText="One Time Payment" controlSubText="Pay now" leftIcon="DollarHandIcon" onClick={()=>navigate("/home/onetimepayment/onetimeform/"+cardID)} />
      <ControlView controlText="Autopay ON" controlSubText="Minimum Due"  onClick={() => {navigate("/home/autopay/enterautopay/"+cardID)}}  leftIcon="RefreshIcon" />
      <ControlView controlText="Manage Payment Accounts" onClick={()=>navigate("/home/managepaymentaccounts/managepayments/"+cardID)} controlSubText="Add or delete your payment accounts" leftIcon="PersonGearIcon" />

      <MainContentSection text="Payment activity">
      {paymentRows}
      </MainContentSection>

    </MainContentContainer> 
  );
}

/**
 * @function PaymentActivityRow
 * @param {String} date - Allows us to set the payment date
 * @param {String} amount - Allows us to set the payment amount
 * @param {String} status - Allows us to set the payment status
 * @param {String} paymentType - Allows us to set the payment type
 * @param {String} accountType - Allows us to set the payment account type
 * @param {String} accountLastFour - Allows us to set the payment account last four digits
 * @param {String} fiName - Allows us to set the payment Financial Institution Name
 * @return {React.Component}
 * @exported false
 * @description The PaymentActivityRow shows the row that is an accordion on the PaymentsPage
 * @example
 * <PaymentActivityRow date="MMM DD, YYYY" amount="$10.00" status="Processing" paymentType="One-Time Payment" accountType="Checking" accountLastFour="*1234" fiName="FIName" />
 */

function PaymentActivityRow(props) {
  const globalController = useBCMSContext();
  const payment = props.payment;
  const date = payment.purchaseDate.format("M d, Y ") ?? "[MMMM DD, YYYY]";
  const amount = payment.amount.format() ?? "[$X.XX]";
  let status = payment.status ?? "";
  const paymentType = payment.type =="recurring" ?"One-time payment" : "One-time payment";
  const accountType = payment.account.type ?? "[Checking]";
  const accountLastFour = payment.account.last_four_digit_number ?? "[*1234]";
  const fiName = payment.account.fi_name ?? "[FIName]";
  const canceledDate = payment.canceledDate? payment.canceledDate.format("M d, Y "): "[canceledDate]";
  const isCanceled = payment.isCanceled;
  const [getToggleRow, setTogglerow] = useState(false);
  BCMSRecord.globalControllerFor([Transaction ], globalController);
  const { close } = useBCMSModal();
  const cancelClicked = () => {
   const transaction = Transaction.initByID(payment.id);
   transaction.isCanceled = true;
   BCMSRecord.batch([
    Transaction.update(transaction),
  
    
   
  ],globalController, Classes);
  close("CancelPaymentModal")
  
  }

  return(
    <div className="payment-activity-row">
     <CustomAccordion>  
      <CustomAccordionToggle onClick={setTogglerow}> 
            <div className="payment-activity-details-row">
              <div className="payment-activity-details-row-container flex-container flex-horizontal align-center-container">
                <p className="set-width">{date}</p>
                <div className="flex-width text-right">
                  <p>{amount}</p>
                  <p className="sub-text">{status}</p>
                </div>
               { getToggleRow ? <SVGAsset id="UpChevronIcon" className="set-width right-icon-container chevron-icon" />:
                 <SVGAsset id="DownChevronIcon" className="set-width right-icon-container down-chevron-icon" />}
              </div>
            </div>
       </CustomAccordionToggle> 
        <CustomAccordionContent>
            <div className="payment-activity-details-content">
              <div className="payment-activity-detail-item flex-container flex-horizontal align-center-container column-space-between-container">
                <p>Payment</p>
                <p>{paymentType}</p>
              </div>
              <div className="payment-activity-detail-item flex-container flex-horizontal align-center-container column-space-between-container">
                <p>Financial Institution</p>
                <p>{fiName}</p>
              </div>
              <div className="payment-activity-detail-item flex-container flex-horizontal align-center-container column-space-between-container">
                <p>Account</p>
                <p className="capitalize">{accountType} {accountLastFour}</p>
              </div>
            {  isCanceled &&  <div className="payment-activity-detail-item flex-container flex-horizontal align-center-container column-space-between-container">
                <p>Canceled Date</p>
                <p>{canceledDate }</p>
              </div>}
              {(status !== "Posted" && isCanceled !== true) && <ModalOpen id="CancelPaymentModal" data={{"CancelClicked":cancelClicked}}> <UICancelButton text="Cancel Payment" className="bold-text" /></ModalOpen>}
            </div>
        </CustomAccordionContent> 
     </CustomAccordion> 
    </div>
  );
}